import { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from '../../store';
import {
    fetchStores,
    updateStoreLiveStatus,
    setSelectedStoreBrand,
    useStoreManagementStores,
    useStoreManagementSelectedBrand,
    useStoreManagementLoading,
    useStoreManagementError,
    useStoreManagementUpdatingIds,
} from '../../store/slices/storeManagementSlice';
import { searchStoreBrands } from '../../services/searchService';
import { BrandSearchSection, StatusCountCards, StoreListSection, EmptyState } from './components';
import BrandSettingsModal from './components/BrandSettingsModal';
import AllUsersEventModal from './components/AllUsersEventModal';
import StoreModal from './components/StoreModal/StoreModal';
import type { StoreBrandSearchResult, StoreLiveStatus, StoreSearchResult } from '../../types/api.types';

export default function StoreManagement() {
    const dispatch = useAppDispatch();

    const stores = useStoreManagementStores();
    const selectedStoreBrand = useStoreManagementSelectedBrand();
    const loading = useStoreManagementLoading();
    const error = useStoreManagementError();
    const updatingStoreIds = useStoreManagementUpdatingIds();

    const [brandSearchValue, setBrandSearchValue] = useState<StoreBrandSearchResult | null>(null);
    const [storeModalStore, setStoreModalStore] = useState<StoreSearchResult | null>(null);
    const [brandSettingsOpen, setBrandSettingsOpen] = useState(false);
    const [allUsersEventOpen, setAllUsersEventOpen] = useState(false);

    useEffect(() => {
        if (selectedStoreBrand?._id) {
            dispatch(fetchStores(selectedStoreBrand._id));
        }
    }, [selectedStoreBrand, dispatch]);

    const handleBrandSelect = useCallback(
        (brand: StoreBrandSearchResult | null) => {
            setBrandSearchValue(brand);
            dispatch(setSelectedStoreBrand(brand));
        },
        [dispatch]
    );

    const handleBrandSearch = useCallback(async (searchText: string) => {
        return await searchStoreBrands(searchText);
    }, []);

    const handleStatusChange = useCallback(
        (storeId: string, newStatus: StoreLiveStatus) => {
            if (!selectedStoreBrand?._id) return;
            dispatch(
                updateStoreLiveStatus({
                    storeId,
                    storeStatus: newStatus,
                    storeBrandId: selectedStoreBrand._id,
                })
            );
        },
        [dispatch, selectedStoreBrand]
    );

    const handleOpenStoreSettings = useCallback((store: StoreSearchResult) => {
        setStoreModalStore(store);
    }, []);

    const handleOpenBrandSettings = useCallback(() => {
        setBrandSettingsOpen(true);
    }, []);

    const handleOpenBrandSettingsFromStore = useCallback(() => {
        setStoreModalStore(null);
        setBrandSettingsOpen(true);
    }, []);

    const handleOpenAllUsersEvent = useCallback(() => {
        setAllUsersEventOpen(true);
    }, []);

    return (
        <div className="w-full space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-2">
                <h2 className="text-4xl font-bold text-[#1e293b] leading-tight">
                    Store Management
                </h2>
                <p className="text-lg text-[#64748b]">
                    Search by store brand and manage store live status
                </p>
            </div>

            {/* Brand Search */}
            <BrandSearchSection
                value={brandSearchValue}
                onSelect={handleBrandSelect}
                onSearch={handleBrandSearch}
                onOpenBrandSettings={handleOpenBrandSettings}
                onOpenAllUsersEvent={handleOpenAllUsersEvent}
            />

            {/* Error Message */}
            {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                    <div className="flex items-center gap-2">
                        <svg
                            className="w-5 h-5 text-red-500 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading === 'loading' && selectedStoreBrand && (
                <div className="flex items-center justify-center py-8">
                    <div className="flex items-center gap-3">
                        <div className="w-6 h-6 border-2 border-[#f59e0b] border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm text-[#64748b]">Loading stores...</span>
                    </div>
                </div>
            )}

            {/* Status Count Cards + Store List - Only show when brand is selected */}
            {selectedStoreBrand && loading !== 'loading' && (
                <>
                    <StatusCountCards stores={stores} />
                    <StoreListSection
                        stores={stores}
                        updatingStoreIds={updatingStoreIds}
                        onStatusChange={handleStatusChange}
                        onOpenSettings={handleOpenStoreSettings}
                    />
                </>
            )}

            {/* Empty State - No brand selected */}
            {!selectedStoreBrand && <EmptyState />}

            {/* Store Modal */}
            <StoreModal
                isOpen={storeModalStore !== null}
                store={storeModalStore}
                storeBrandId={selectedStoreBrand?._id ?? ''}
                onClose={() => setStoreModalStore(null)}
                onOpenBrandSettings={handleOpenBrandSettingsFromStore}
            />

            {/* Brand Settings Modal */}
            {selectedStoreBrand && (
                <BrandSettingsModal
                    isOpen={brandSettingsOpen}
                    storeBrandId={selectedStoreBrand._id}
                    brandName={selectedStoreBrand.name}
                    onClose={() => setBrandSettingsOpen(false)}
                />
            )}

            {/* All Users Event Modal */}
            {selectedStoreBrand && (
                <AllUsersEventModal
                    isOpen={allUsersEventOpen}
                    storeBrandId={selectedStoreBrand._id}
                    onClose={() => setAllUsersEventOpen(false)}
                />
            )}
        </div>
    );
}
